// Geometrías low-poly del mundo, generadas por código (sin archivos externos).
// Cada geometría lleva colores por vértice y se dibuja con sombreado plano (facetado).
// No usa el DOM: también se carga en las pruebas de Node.
import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createRandom } from '../core/random.js';

// Semilla fija: las formas son siempre las mismas en cada partida.
const rand = createRandom(20261017);
const _c = new THREE.Color();

/** Deforma los vértices al azar (sin abrir grietas entre caras). */
function jitter(geom, amount) {
  geom.deleteAttribute('normal');
  geom.deleteAttribute('uv');
  const g = mergeVertices(geom);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) {
    p.setXYZ(
      i,
      p.getX(i) + (rand() - 0.5) * amount,
      p.getY(i) + (rand() - 0.5) * amount,
      p.getZ(i) + (rand() - 0.5) * amount,
    );
  }
  return g;
}

/** Ajusta la geometría a una caja unidad centrada (así encaja en las cajas de colisión). */
function fitUnit(geom) {
  geom.computeBoundingBox();
  const b = geom.boundingBox;
  const size = b.getSize(new THREE.Vector3());
  const center = b.getCenter(new THREE.Vector3());
  geom.translate(-center.x, -center.y, -center.z);
  geom.scale(1 / size.x, 1 / size.y, 1 / size.z);
  return geom;
}

/**
 * Colorea cada cara: fn(color, normal, centroY) decide su color.
 * Devuelve una geometría no indexada con normales planas y atributo 'color'.
 */
function paint(geom, fn) {
  const g = geom.index ? geom.toNonIndexed() : geom;
  g.deleteAttribute('uv');
  g.computeVertexNormals();
  const n = g.attributes.normal;
  const p = g.attributes.position;
  const colors = new Float32Array(n.count * 3);
  const normal = new THREE.Vector3();
  for (let i = 0; i < n.count; i += 3) {
    normal.set(n.getX(i) + n.getX(i + 1) + n.getX(i + 2), n.getY(i) + n.getY(i + 1) + n.getY(i + 2), n.getZ(i) + n.getZ(i + 1) + n.getZ(i + 2)).normalize();
    fn(_c, normal, (p.getY(i) + p.getY(i + 1) + p.getY(i + 2)) / 3);
    for (let k = 0; k < 3; k++) _c.toArray(colors, (i + k) * 3);
  }
  g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return g;
}

/** Color con una variación aleatoria de brillo por cara. */
const vary = (hex, spread = 0.12) => (c) => c.setHex(hex).multiplyScalar(1 - spread / 2 + rand() * spread);

/** Material para todas las geometrías de este módulo. */
export function lowPolyMaterial(extra = {}) {
  return new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, ...extra });
}

// ---------- Camino ----------

/** Losa de piedra: cara superior clara, laterales oscuros (marcan las juntas). */
export function tileGeometry() {
  return paint(new THREE.BoxGeometry(1, 1, 1), (c, n) => c.setHex(0x8a8577).multiplyScalar(n.y > 0.9 ? 1 : 0.45));
}

/** Pilar de piedra bajo el camino: altura 1, centrado (segmentBox coloca cajas centradas). */
export function pillarGeometry() {
  const g = jitter(new THREE.CylinderGeometry(0.45, 0.6, 1, 7, 4), 0.06);
  return paint(g, vary(0x4b525a, 0.2));
}

// ---------- Obstáculos ----------

/** Tronco caído (eje a lo ancho del camino): corteza oscura y cortes claros. */
export function logGeometry() {
  const g = fitUnit(jitter(new THREE.CylinderGeometry(0.5, 0.5, 1, 9, 2).rotateZ(Math.PI / 2), 0.05));
  return paint(g, (c, n) => {
    if (Math.abs(n.x) > 0.8) c.setHex(0xc9a46c).multiplyScalar(0.9 + rand() * 0.15);
    else c.setHex(0x5b3b22).multiplyScalar(0.8 + rand() * 0.35);
  });
}

/** Zarza colgante (un carril de ancho): rama gruesa con espinas rojas y hojas. */
export function brambleGeometry() {
  const parts = [];
  parts.push(paint(jitter(new THREE.CylinderGeometry(0.3, 0.3, 1.3, 7, 3).rotateZ(Math.PI / 2), 0.08), vary(0x4a3424, 0.25)));
  for (let i = 0; i < 9; i++) {
    const a = rand() * Math.PI * 2;
    const thorn = new THREE.ConeGeometry(0.07, 0.32, 4);
    thorn.translate(0, 0.3, 0);
    thorn.rotateX(Math.cos(a) * 1.2);
    thorn.rotateZ(Math.sin(a) * 1.2);
    thorn.translate(-0.55 + i * 0.14, 0, 0);
    parts.push(paint(thorn, vary(0xd23a2a, 0.2)));
  }
  for (let i = 0; i < 4; i++) {
    const leaf = new THREE.IcosahedronGeometry(0.16 + rand() * 0.08, 0);
    leaf.translate(-0.5 + rand(), (rand() - 0.3) * 0.4, (rand() - 0.5) * 0.4);
    parts.push(paint(leaf, vary(0x2f5a2c, 0.3)));
  }
  return fitUnit(mergeGeometries(parts));
}

/** Liana vertical (sostiene la zarza): cilindro fino de altura 1, centrado. */
export function vineGeometry() {
  return paint(new THREE.CylinderGeometry(0.5, 0.5, 1, 5), vary(0x2c4a25, 0.2));
}

/** Roca: icosaedro deformado; las caras de arriba llevan musgo. */
export function rockGeometry() {
  const g = fitUnit(jitter(new THREE.IcosahedronGeometry(1, 1), 0.38));
  return paint(g, (c, n) => {
    if (n.y > 0.55) c.setHex(0x587a3c).multiplyScalar(0.85 + rand() * 0.25);
    else c.setHex(0x7d8590).multiplyScalar(0.75 + rand() * 0.3);
  });
}

// ---------- Bosque ----------

/** Tronco de árbol: altura 1, centrado. */
export function trunkGeometry() {
  return paint(jitter(new THREE.CylinderGeometry(0.32, 0.5, 1, 7, 3), 0.03), vary(0x3a2a1e, 0.25));
}

/** Copa de conífera: tres conos apilados. */
export function canopyGeometry() {
  const parts = [
    [1, 0.9, 0.45],
    [0.78, 0.8, 0.95],
    [0.52, 0.75, 1.42],
  ].map(([r, h, y]) => jitter(new THREE.ConeGeometry(r, h, 7, 1).translate(0, y, 0), 0.12));
  const g = mergeGeometries(parts.map((p) => p.toNonIndexed()));
  // Normalizada: diámetro 1 y altura 1; se pinta con la base en y = 0 y luego se centra como las cajas de segmentBox.
  g.computeBoundingBox();
  const size = g.boundingBox.getSize(new THREE.Vector3());
  g.translate(0, -g.boundingBox.min.y, 0).scale(1 / size.x, 1 / size.y, 1 / size.z);
  return paint(g, (c, n, y) => {
    c.setHex(0x1d3a27).lerp(_tip.setHex(0x3a6b40), Math.min(1, y) * 0.6 + Math.max(0, n.y) * 0.25);
    c.multiplyScalar(0.85 + rand() * 0.25);
  }).translate(0, -0.5, 0);
}
const _tip = new THREE.Color();

// ---------- Faroles y brillos ----------

/** Poste de farol (tamaño real): baja hasta la bruma y sostiene un farol a 2,4 m. */
export function lampPostGeometry() {
  const pole = new THREE.CylinderGeometry(0.06, 0.09, 6, 6).translate(0, -0.6, 0);
  const cap = new THREE.ConeGeometry(0.2, 0.18, 6).translate(0, 2.7, 0);
  const base = new THREE.CylinderGeometry(0.16, 0.16, 0.08, 6).translate(0, 2.2, 0);
  return mergeGeometries([paint(pole, vary(0x2a221c, 0.2)), paint(cap, vary(0x2a221c, 0.1)), paint(base, vary(0x3a2e24, 0.1))]);
}

/** Cristal encendido del farol (se dibuja sin iluminación: siempre brilla). */
export function lampGlowGeometry() {
  return new THREE.BoxGeometry(0.22, 0.34, 0.22).translate(0, 2.43, 0);
}

/** Haz de luz vertical (cilindro abierto, base en y = 0). */
export function beamGeometry() {
  return new THREE.CylinderGeometry(0.28, 0.4, 7, 10, 1, true).translate(0, 3.5, 0);
}

/** Aro brillante sobre el suelo. */
export function ringGeometry() {
  return new THREE.RingGeometry(0.42, 0.62, 24).rotateX(-Math.PI / 2);
}

/** Material aditivo para brillos (el color va por instancia). */
export function additiveMaterial(opacity) {
  return new THREE.MeshBasicMaterial({
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

// ---------- Objetos ----------

/** Moneda dorada (de canto hacia el jugador). */
export function coinGeometry() {
  const g = new THREE.CylinderGeometry(0.32, 0.32, 0.08, 12).rotateX(Math.PI / 2);
  return paint(g, (c, n) => c.setHex(Math.abs(n.z) > 0.9 ? 0xffd23a : 0xc8901c));
}

/** Gema roja facetada (vale 5 monedas). */
export function gemGeometry() {
  return paint(new THREE.OctahedronGeometry(0.3, 0).scale(1, 1.4, 1), vary(0xff3355, 0.35));
}

/** Imán en forma de herradura con las puntas blancas. */
export function magnetGeometry() {
  const arc = new THREE.TorusGeometry(0.28, 0.1, 6, 10, Math.PI).rotateZ(Math.PI);
  const tips = [-0.28, 0.28].map((x) => new THREE.BoxGeometry(0.2, 0.18, 0.2).translate(x, 0.08, 0));
  return mergeGeometries([paint(arc, vary(0xb04cff, 0.15)), ...tips.map((t) => paint(t, vary(0xf2f2f2, 0.1)))]);
}

/** Escudo: gema facetada. */
export function shieldGeometry() {
  return paint(new THREE.OctahedronGeometry(0.42, 0).scale(1, 1.25, 0.7), vary(0x4cd8ff, 0.3));
}

/** Sprint: rayo. */
export function sprintGeometry() {
  const s = new THREE.Shape();
  [
    [0.08, 0.5],
    [-0.22, 0.02],
    [-0.02, 0.02],
    [-0.1, -0.5],
    [0.22, 0.04],
    [0.02, 0.04],
  ].forEach(([x, y], i) => (i ? s.lineTo(x, y) : s.moveTo(x, y)));
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.12, bevelEnabled: false }).translate(0, 0, -0.06);
  return paint(g, (c, n) => c.setHex(Math.abs(n.z) > 0.9 ? 0xffa53a : 0xd2600c));
}
